import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, signal, ViewChild, Output, EventEmitter, Input, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CoreService } from '../../../services/core.service';
import { ContentSiteI, SocialPlatformI } from '../../../interfaces/core.interface';
import { FollowersChartComponent } from '../followers-chart/followers-chart.component';
import { SocialAiTrendsChartComponent } from "../social-ai-trends-chart/social-ai-trends-chart.component";
import { SocialAiFormatBreakdownChartComponent } from "../social-ai-format-breakdown-chart/social-ai-format-breakdown-chart.component";
import { SocialAiHeatmapChartComponent } from "../social-ai-heatmap-chart/social-ai-heatmap-chart.component";
import { SocialAiEngagementChartComponent } from '../social-ai-engagement-chart/social-ai-engagement-chart.component';
import { SocialAiReachChartComponent } from '../social-ai-reach-chart/social-ai-reach-chart.component';
import { SocialAiImpressionsChartComponent } from '../social-ai-impressions-chart/social-ai-impressions-chart.component';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

interface Column {
  key: string;
  label: string;
}

interface Row {
  [key: string]: any;
}

@Component({
  selector: 'app-social-ai-reports-chart',
  standalone: true,
  imports: [CommonModule, FormsModule, FollowersChartComponent,
    SocialAiTrendsChartComponent, SocialAiFormatBreakdownChartComponent,
    SocialAiHeatmapChartComponent, SocialAiEngagementChartComponent, SocialAiReachChartComponent, SocialAiImpressionsChartComponent],
  templateUrl: './social-ai-reports-chart.component.html',
  styleUrl: './social-ai-reports-chart.component.scss',
})

export class SocialAiReportsChartComponent implements OnInit {

  constructor(private coreService: CoreService, private snack: MatSnackBar) { }

  @ViewChild(FollowersChartComponent) followersChart!: FollowersChartComponent;
  @ViewChild(SocialAiReachChartComponent) reachChart!: SocialAiReachChartComponent;
  @ViewChild(SocialAiEngagementChartComponent) engagementChart!: SocialAiEngagementChartComponent;
  @ViewChild(SocialAiImpressionsChartComponent) impressionsChart!: SocialAiImpressionsChartComponent;
  @ViewChild(SocialAiFormatBreakdownChartComponent) formatChart!: SocialAiFormatBreakdownChartComponent;
  @ViewChild(SocialAiHeatmapChartComponent) heatmapChart!: SocialAiHeatmapChartComponent;
  @Input() viewMode!: Signal<'query' | 'metrics'>;
  @Output() goQuery = new EventEmitter<void>();
  @Output() goMetrics = new EventEmitter<void>();

  // =========================
  // Dropdown state
  // =========================
  companyID = signal<number>(0);

  sites = signal<ContentSiteI[]>([]);
  platformsAll = signal<SocialPlatformI[]>([]);
  pages = signal<any[]>([]); //

  selectedSiteId = signal<number | null>(null);
  selectedPlatformId = signal<number | null>(null);

  selectedPeriod = signal<'days_28' | 'week' | 'custom'>('days_28');
  customFrom = signal('2024-04-01');
  customTo = signal('2024-04-30');

  formatOptions = signal<string[]>([]);
  selectedFormat = signal<string | null>(null);
  activeCharts = signal<string[]>([]);
  columns = signal<Column[]>([]);
  sortedRows = signal<Row[]>([]);
  visibleColumns = signal<Set<string>>(new Set());
  metricsData = signal<any[]>([]);
  isInstagramPlatform = false;

  chartOptions = [
    { key: 'followers', label: 'Followers' },
    { key: 'reach', label: 'Reach' },
    { key: 'engagement', label: 'Engagement' },
    { key: 'impressions', label: 'Impressions' },
    { key: 'format', label: 'Format Breakdown' },
    { key: 'heatmap', label: 'Heatmap' },
  ];

  // =========================
  // Init
  // =========================
  ngOnInit(): void {
    this.companyID.set(this.coreService.user?.companyID || 0);
    this.bootstrapDropdowns();
  }

  private bootstrapDropdowns(): void {
    forkJoin({
      sites: this.loadSites(),
      platforms: this.loadPlatforms(),
    }).subscribe(({ sites, platforms }) => {
      this.sites.set(sites || []);
      this.platformsAll.set(platforms || []);

      const firstSite = this.sites().length ? this.sites()[0] : null;
      this.selectedSiteId.set(firstSite?.ID ?? null);

      this.setDefaultPlatformForSite();
      this.loadVisibleColumns();
      this.activeCharts.set([...this.visibleColumns()]);
    });
  }

  // =========================
  // Computed
  // =========================
  filteredPlatforms = computed(() => {
    const siteId = this.selectedSiteId();
    if (!siteId) return [];

    return this.platformsAll().filter(p => {
      if (p.site_id !== siteId) return false;
      const name = (p.platform || '').toLowerCase();
      return name.includes('facebook') || name.includes('instagram') || name === 'ig';
    });
  });

  // =========================
  // Handlers
  // =========================
  onSiteChange(siteId: number): void {
    this.selectedSiteId.set(siteId);
    this.setDefaultPlatformForSite();
  }

  onPlatformChange(platformId: number): void {
    this.selectedPlatformId.set(platformId);
    this.buildPagesFromPlatform();

    const platform = this.platformsAll().find(p => p.id === platformId);
    const name = (platform?.platform || '').toLowerCase();

    this.isInstagramPlatform =
      name.includes('instagram') || name === 'ig';

    if (this.isInstagramPlatform) {
      this.activeCharts.set(
        this.activeCharts().filter(
          k => k !== 'engagement' && k !== 'impressions'
        )
      );
    }
  }

  onPeriodChange(period: 'days_28' | 'week' | 'custom'): void {
    this.selectedPeriod.set(period);
  }

  // =========================
  // Helpers
  // =========================
  private setDefaultPlatformForSite(): void {
    const list = this.filteredPlatforms();

    if (!list.length) {
      this.selectedPlatformId.set(null);
      this.pages.set([]);
      this.isInstagramPlatform = false;
      return;
    }

    const facebook = list.find(p =>
      (p.platform || '').toLowerCase().includes('facebook')
    );

    const instagram = list.find(p =>
      (p.platform || '').toLowerCase().includes('instagram') ||
      (p.platform || '').toLowerCase() === 'ig'
    );

    const selected = facebook || instagram || list[0];

    this.selectedPlatformId.set(selected.id);
    this.buildPagesFromPlatform();

    const name = (selected.platform || '').toLowerCase();
    this.isInstagramPlatform =
      name.includes('instagram') || name === 'ig';
  }


  fmtNumber(value: number | null | undefined): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('en-US').format(value);
  }

  fmtPct(value: number | null | undefined): string {
    if (value == null) return '—';
    return `${value.toFixed(2)}%`;
  }

  private loadSites() {
    return this.coreService.getData(`/querybuilder/websites-feeds-dropdown/${this.companyID()}`).pipe(
      take(1),
      map((res: any) => (res.data || []).filter((s: any) => s.hasFeed)),
      catchError(() => of([]))
    );
  }

  private loadPlatforms() {
    return this.coreService.getData('/querybuilder/platform-dropdown').pipe(
      take(1),
      map((res: any) =>
        (res.data || []).filter(
          (p: SocialPlatformI) =>
            p.isApproved === 'Approved' && !!p.access_token
        )
      ),
      catchError(() => of([]))
    );
  }

  isInstagramSelected = computed(() => {
    const pid = this.selectedPlatformId();
    if (!pid) return false;

    const p = this.platformsAll().find(x => x.id === pid);
    const name = (p?.platform || '').toLowerCase();

    return name.includes('instagram') || name === 'ig';
  });

  private buildPagesFromPlatform(): void {
    const platformId = this.selectedPlatformId();
    if (!platformId) {
      this.pages.set([]);
      return;
    }

    const platform = this.platformsAll().find(p => p.id === platformId);
    if (!platform) {
      this.pages.set([]);
      return;
    }

    this.pages.set([
      {
        social_media_id: platform.social_media_id,
        access_token: platform.access_token,
        platform: platform.platform,
      },
    ]);
  }

  toggleChart(chartKey: string, checked: boolean): void {
    const current = this.activeCharts();

    if (checked) {
      this.activeCharts.set([...current, chartKey]);
    } else {
      this.activeCharts.set(current.filter(c => c !== chartKey));
    }
    this.toggleColumn(chartKey, checked);
  }

  toggleColumn(key: string, checked: boolean) {
    const next = new Set(this.visibleColumns());

    if (checked) {
      next.add(key);
    } else {
      next.delete(key);
    }

    this.visibleColumns.set(next);
  }

  onChartData(data: any[]): void {
    this.metricsData.set(data);
  }

  private async loadFontAsBase64(path: string): Promise<string> {
    const res = await fetch(path);
    if (!res.ok) throw new Error(`Font fetch failed: ${path} (${res.status})`);

    const buf = await res.arrayBuffer();

    const b = new Uint8Array(buf.slice(0, 4));
    const sig = String.fromCharCode(...b);

    const isOTF = sig === 'OTTO' || sig === 'ttcf';
    const isTTF = b[0] === 0x00 && b[1] === 0x01 && b[2] === 0x00 && b[3] === 0x00;

    if (!isOTF && !isTTF) {
      throw new Error(`Not a font file: ${path} (signature="${sig}")`);
    }

    let binary = '';
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;

    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }

    return btoa(binary);
  }

  private async ensureGreekFont(doc: jsPDF) {
    const reg = await this.loadFontAsBase64('assets/fonts/greek/NotoSans-Regular.ttf');
    doc.addFileToVFS('NotoSans-Regular.ttf', reg);
    doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');

    const bold = await this.loadFontAsBase64('assets/fonts/greek/NotoSans-Bold.ttf');
    doc.addFileToVFS('NotoSans-Bold.ttf', bold);
    doc.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold');

    doc.setFont('NotoSans', 'normal');
  }

  async bulkExportPdf() {
    try {

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      const margin = 40;
      const pageNumFont = 'NotoSans';
      const pageNumSize = 9;
      const pageNumMarginBottom = 16;

      await this.ensureGreekFont?.(doc);

      const pageW = doc.internal.pageSize.getWidth();
      const usableWidth = pageW - 80;
      const chartHeight = 300;

      const periodMap: Record<string, string> = {
        days_28: 'Last 30 days',
        week: 'Last 7 days',
        custom: 'Custom range'
      };

      const periodLabel = periodMap[this.selectedPeriod() || ''] || '—';

      const website =
        this.sites().find(s => s.ID === this.selectedSiteId())?.Domain || '—';

      const platform =
        this.platformsAll().find(p => p.id === this.selectedPlatformId())?.platform || '—';

      const today = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });

      const chartsToExport = [
        {
          key: 'followers',
          title: 'Followers Report',
          instance: this.followersChart,
          metricValue: this.followersChart?.metricValue
        },
        {
          key: 'reach',
          title: 'Reach Report',
          instance: this.reachChart,
          metricValue: this.reachChart?.metricValue
        },
        {
          key: 'engagement',
          title: 'Engagement Report',
          instance: this.engagementChart,
          metricValue: this.engagementChart?.metricValue
        },
        {
          key: 'impressions',
          title: 'Impressions Report',
          instance: this.impressionsChart,
          metricValue: this.impressionsChart?.metricValue
        },
        {
          key: 'format',
          title: 'Format Breakdown Report',
          instance: this.formatChart,
          metricValue: null
        },
        {
          key: 'heatmap',
          title: 'Heatmap Report',
          instance: this.heatmapChart,
          metricValue: null
        }
      ];

      let firstPage = true;

      for (const chart of chartsToExport) {

        if (!this.activeCharts().includes(chart.key)) continue;
        if (!chart.instance) continue;

        if (!firstPage) doc.addPage();
        firstPage = false;

        const imgData = await chart.instance.exportChartImage();

        // =============================
        // TITLE
        // =============================
        doc.setFont('NotoSans', 'bold');
        doc.setFontSize(14);
        doc.text(chart.title, 40, 34);

        // =============================
        // LOGO
        // =============================
        try {
          const logoDataUrl = await this.loadImageAsDataUrl('assets/images/publio_logo_pdf.png');
          const logoSize = 60;
          doc.addImage(logoDataUrl, 'PNG', pageW - 40 - logoSize, 18, logoSize, logoSize);
        } catch { }

        // =============================
        // META BLOCK
        // =============================
        doc.setFontSize(9);

        const leftX = 40;
        const labelW = 110;
        const valueX = leftX + labelW;

        const currentValue =
          chart.metricValue != null
            ? chart.metricValue.toLocaleString()
            : '—';

        const meta: Array<[string, string]> = [
          ['Website:', website],
          ['Platform:', platform],
          ['Date Range:', periodLabel],
          ['Current Value:', `${currentValue} (on ${today})`]
        ];

        let y = 55;
        const lineH = 15;

        for (const [label, value] of meta) {
          doc.setFont('NotoSans', 'normal');
          doc.text(label, leftX, y);

          doc.setFont('NotoSans', 'bold');
          doc.text(value, valueX, y);

          y += lineH;
        }

        // =============================
        // CHART
        // =============================
        doc.addImage(
          imgData,
          'PNG',
          40,
          y + 60,
          usableWidth,
          chartHeight
        );

        if (chart.key === 'format' && this.formatChart?.formats?.length) {

          const head = [[
            '',
            'Engagement',
            ...(this.formatChart.showSaves ? ['Saves'] : []),
            'Clicks'
          ]];

          const body = this.formatChart.formats.map((f: any) => [
            f.name,
            `${f.engagement?.toFixed(2)}% (${f.engagementValue?.toLocaleString()})`,
            ...(this.formatChart.showSaves
              ? [`${f.saves?.toFixed(2)}% (${f.savesValue?.toLocaleString()})`]
              : []),
            `${f.clicks?.toFixed(2)}% (${f.clicksValue?.toLocaleString()})`
          ]);

          autoTable(doc, {
            startY: y + 15 + chartHeight + 20,
            head,
            body,
            styles: {
              font: 'NotoSans',
              fontSize: 9,
              cellPadding: 4
            },
            headStyles: {
              font: 'NotoSans',
              fontStyle: 'bold'
            }
          });
        }

      }

      // Page numbers
      const pageCount = doc.getNumberOfPages();

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        doc.setFont(pageNumFont, 'normal');
        doc.setFontSize(pageNumSize);

        doc.text(
          `Page ${i} of ${pageCount}`,
          pageW - margin,
          pageH - pageNumMarginBottom,
          { align: 'right' }
        );
      }

      doc.save('social-ai-report.pdf');

    } catch (e) {
      console.error('PDF export failed:', e);
      alert('PDF export failed.');
    }
  }

  private async loadImageAsDataUrl(path: string): Promise<string> {
    const res = await fetch(path);
    if (!res.ok) throw new Error(`Image fetch failed: ${path} (${res.status})`);
    const blob = await res.blob();

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(blob);
    });
  }

  openSavedView() {
    this.saveVisibleColumns();
    this.snack.open('Saved View Stored!', 'OK', {
      duration: 2500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['qb-snack-success'],
    });
  }

  private savedViewKey(): string {
    const cid = this.companyID() || 0;
    return `reports_visible_columns:${cid}`;
  }

  private saveVisibleColumns(): void {
    const cols = Array.from(this.visibleColumns());
    localStorage.setItem(this.savedViewKey(), JSON.stringify(cols));
  }

  private loadVisibleColumns(): void {
    const raw = localStorage.getItem(this.savedViewKey());
    if (!raw) return;

    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) this.visibleColumns.set(new Set(arr));
    } catch {
    }
  }
}
